import { useReportViewContext } from '@/app/features/reports/components/report-viewer-context';
import React, { createContext, useCallback, useMemo, useState } from 'react';

interface ReportSelectionContext {
    bulkEditMode: boolean;
    setBulkEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    selectedItems: Set<string>;
    setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
    selectAll: () => void;
    deselectAll: () => void;
}

const ReportSelectionContext = createContext<ReportSelectionContext>(null);

export const useReportSelectionContext = () => {
    const context = React.useContext(ReportSelectionContext);
    if (!context) throw new Error('useReportSelectionContext must be used within a ReportSelectionProvider');
    return context;
}

interface ReportSelectionProviderProps {
    children: React.ReactNode;
}

export const ReportSelectionProvider: React.FC<ReportSelectionProviderProps> = ({ children }) => {
    const [bulkEditMode, setBulkEditMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    const { filteredData } = useReportViewContext();

    const selectAll = useCallback(() => {
        setBulkEditMode(true);
        setSelectedItems(new Set(filteredData.map(item => item.assessmentTestId.toString())));
    }, [filteredData]);

    const deselectAll = useCallback(() => {
        setSelectedItems(new Set());
        setBulkEditMode(false);
    }, []);

    const value = useMemo(() => ({
        bulkEditMode,
        setBulkEditMode,
        selectedItems,
        setSelectedItems,
        selectAll,
        deselectAll
    }), [bulkEditMode, selectedItems]);

    return (
        <ReportSelectionContext.Provider value={value}>
            {children}
        </ReportSelectionContext.Provider>
    );
};

ReportSelectionProvider.displayName = 'ReportSelectionProvider';